<!-- DateInput.vue -->
<template>
  <div role="textbox" class="date-input" :class="hostClasses" @focusout="focusOut">
    <input
      ref="a"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      :value="aval"
      :class="{ year: isYearRef('a') }"
      @focus="inputFocus('a', $event)"
      @blur="inputBlur('a')"
      @input="inputValue('a', $event)"
      @keydown="inputKey('a', $event)"
      @paste.prevent="inputPaste('a', $event)"
    >
    <span ref="sep1">/</span>
    <input
      ref="b"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      :value="bval"
      :class="{ year: isYearRef('b') }"
      @focus="inputFocus('b', $event)"
      @blur="inputBlur('b')"
      @input="inputValue('b', $event)"
      @keydown="inputKey('b', $event)"
      @paste.prevent="inputPaste('b', $event)"
    >
    <span ref="sep1">/</span>
    <input
      ref="c"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      :value="cval"
      :class="{ year: isYearRef('c') }"
      @focus="inputFocus('c', $event)"
      @blur="inputBlur('c')"
      @input="inputValue('c', $event)"
      @keydown="inputKey('c', $event)"
      @paste.prevent="inputPaste('c', $event)"
    >
  </div>
</template>

<style lang="scss">
  .date-input {
    display:inline-flex;
    gap:0px;
    padding-left: 0 !important;
    box-sizing: border-box;
    
    &.invalid {
      border: 1px solid var(--error-color);
    }

    &> input { 
      font-size: 1em;
      min-width: auto; width: 2ch; 
      height: 100%;
      margin: 0;
      border: 0;
      border-radius: unset;
      overflow: visible;

      margin-left: 0.5rem;
      margin-right: 0.5rem; 
      text-align: center; // overridden elsewhere
            
      &:first-child { text-align: right; }
      
      &.year { width: 6ch;}

      // Hide input[type=number] arrows
      -moz-appearance: textfield;
      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      
      // Selection
      &:focus-visible {
        outline: none;
        border: none;
        background: var(--accent-color);
        border-radius: var(--br-sharp);
      }
    }
    
    // separators
    span {
      line-height: 2em;
      color: var(--text-dim2-color);
    }
  }
</style>

<script>
import { nextTick } from 'vue'

const REF_ORDER = ['a', 'b', 'c']

function digitsOnly(value) {
  return String(value ?? '').replace(/\D/g, '')
}

function expandTwoDigitYear(value) {
  const yearText = digitsOnly(value)
  if (!yearText) return ''
  if (yearText.length > 2) return yearText.slice(0, 4)

  let year = Number(yearText) + 1900
  if (new Date().getFullYear() - year >= 90) {
    year += 100
  }

  return String(year)
}

function parseDateText(value, preferredLayout = 'mdy') {
  const text = String(value ?? '').trim()
  if (!text) return null

  const compact = digitsOnly(text)
  const parts = text.match(/\d+/g)

  if (parts?.length >= 3) {
    let layout = preferredLayout
    if (parts[0].length === 4 || Number(parts[0]) > 31) layout = 'ymd'
    else if (parts[2].length === 4 || Number(parts[2]) > 31) layout = 'mdy'

    let y
    let m
    let d

    if (layout === 'ymd') [y, m, d] = parts
    else [m, d, y] = parts

    if (digitsOnly(y).length <= 2) {
      y = expandTwoDigitYear(y)
    }

    return {
      y: digitsOnly(y).slice(0, 4),
      m: digitsOnly(m).slice(0, 2),
      d: digitsOnly(d).slice(0, 2),
    }
  }

  if (compact.length === 8) {
    if (
      compact.startsWith('19') ||
      compact.startsWith('20') ||
      preferredLayout === 'ymd'
    ) {
      return {
        y: compact.slice(0, 4),
        m: compact.slice(4, 6),
        d: compact.slice(6, 8),
      }
    }

    return {
      m: compact.slice(0, 2),
      d: compact.slice(2, 4),
      y: compact.slice(4, 8),
    }
  }

  if (compact.length === 6) {
    if (preferredLayout === 'ymd' || Number(compact.slice(0, 2)) > 31) {
      return {
        y: expandTwoDigitYear(compact.slice(0, 2)),
        m: compact.slice(2, 4),
        d: compact.slice(4, 6),
      }
    }

    return {
      m: compact.slice(0, 2),
      d: compact.slice(2, 4),
      y: expandTwoDigitYear(compact.slice(4, 6)),
    }
  }

  return null
}

export default {
  name: 'DateInput',
  props: {
    modelValue: {  },
    layout:     { type: String, default: 'mdy' } /* 'ymd' | 'mdy' | 'auto' */
  },
  emits: ['blur', 'update:modelValue'],

  data () {
    return {
      y: '',
      m: '',
      d: '',

      // Cached and validated values
      dateValue: undefined,
      stringValue: '',
      valid: false,
    }
  },

  computed: {
    resolvedLayout() {
      return this.layout === 'ymd' ? 'ymd' : 'mdy'
    },

    hasAnyValue() {
      return [this.y, this.m, this.d].some(v => String(v ?? '').length > 0)
    },

    hostClasses () {
      return { 'invalid': this.hasAnyValue && !this.valid }
    },
    
    aval: {
      get()  { return this.getComponent('a')},
    },
    
    bval: {
      get()  { return this.getComponent('b')},
    },

    cval: {
      get()  { return this.getComponent('c')},
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler (val) {
        if (!val) {
          this.resetState()
          return
        }
        
        if (val instanceof Date) {
          this.setDateParts({
            y: String(val.getFullYear()),
            m: String(val.getMonth() + 1).padStart(2, '0'),
            d: String(val.getDate()).padStart(2, '0'),
          }, { emit: false })
          return
        }

        if (typeof val == 'string') {
          const parsed = parseDateText(val, this.resolvedLayout)
          if (parsed) {
            this.setDateParts(parsed, { emit: false })
            return
          }
        }

        this.resetState()
      }
    },
  },

  methods: {
    focusOut(e) {
      // Fired for every focus change in component.
      // Emit component-level blur if focus goes outside
      const next = e.relatedTarget;
      if (!next || !this.$el.contains(next)) {
        nextTick(() => this.$emit('blur', this.stringValue ?? ''))
      }
    },

    resetState() {
      this.y = ''
      this.m = ''
      this.d = ''
      this.valid = false
      this.dateValue = undefined
      this.stringValue = ''
    },
    
    getComponent(comp) {
      switch (this.resolvedLayout) {
        default:
        case 'mdy': 
          if ('a'===comp) return this.m;
          if ('b'===comp) return this.d;
          if ('c'===comp) return this.y;
          break;
        case 'ymd': 
          if ('a'===comp) return this.y;
          if ('b'===comp) return this.m;
          if ('c'===comp) return this.d;
          break;
      }
      return undefined;
    },
    
    setComponent(comp, val, { emit = true } = {}) {
      const normalized = this.normalizeComponentValue(comp, val)

      switch (this.resolvedLayout) {
        default:
        case 'mdy': 
          if ('a'===comp) this.m = normalized;
          if ('b'===comp) this.d = normalized;
          if ('c'===comp) this.y = normalized;
          break;
        case 'ymd': 
          if ('a'===comp) this.y = normalized;
          if ('b'===comp) this.m = normalized;
          if ('c'===comp) this.d = normalized;
          break;
      }

      this.afterInternalChange({ emit })
    },

    setDateParts(parts, { emit = true } = {}) {
      this.y = digitsOnly(parts?.y).slice(0, 4)
      this.m = digitsOnly(parts?.m).slice(0, 2)
      this.d = digitsOnly(parts?.d).slice(0, 2)
      this.afterInternalChange({ emit })
    },

    afterInternalChange({ emit = true } = {}) {
      this.sync()

      if (!emit) return

      if (this.valid) {
        this.$emit('update:modelValue', this.stringValue)
      }
      else if (!this.hasAnyValue) {
        this.$emit('update:modelValue', '')
      }
    },
    
    isYearRef(ref) {
      switch (this.resolvedLayout) {
        default:
        case 'mdy': 
          if ('c'===ref) return true;
          break;
        case 'ymd': 
          if ('a'===ref) return true;
          break;
      }
      return false;
    },

    isMonthRef(ref) {
      switch (this.resolvedLayout) {
        default:
        case 'mdy':
          return ref === 'a'
        case 'ymd':
          return ref === 'b'
      }
    },

    isDayRef(ref) {
      switch (this.resolvedLayout) {
        default:
        case 'mdy':
          return ref === 'b'
        case 'ymd':
          return ref === 'c'
      }
    },

    componentMaxLength(ref) {
      return this.isYearRef(ref) ? 4 : 2
    },

    shouldAdvanceEarly(ref, nextValue, e) {
      const inserted = digitsOnly(e?.data)
      if (inserted.length !== 1 || nextValue.length !== 1) return false

      if (this.isMonthRef(ref)) {
        return inserted !== '0' && inserted !== '1'
      }

      if (this.isDayRef(ref)) {
        return Number(inserted) >= 4
      }

      return false
    },

    normalizeComponentValue(ref, value) {
      return digitsOnly(value).slice(0, this.componentMaxLength(ref))
    },

    prevRef(ref) {
      const index = REF_ORDER.indexOf(ref)
      return REF_ORDER[index - 1] ?? null
    },

    nextRef(ref) {
      const index = REF_ORDER.indexOf(ref)
      return REF_ORDER[index + 1] ?? null
    },

    focusComponent(ref, selection = 'all') {
      const input = this.$refs[ref]
      if (!input) return

      input.focus()

      nextTick(() => {
        if (input !== document.activeElement) return

        if (selection === 'end') {
          const end = input.value.length
          input.setSelectionRange(end, end)
          return
        }

        if (selection === 'start') {
          input.setSelectionRange(0, 0)
          return
        }

        input.select()
      })
    },
    
    inputFocus(ref, e) {
      nextTick(() => {
        if (e.target === document.activeElement) {
          e.target.select()
        }
      })
    },
    
    inputBlur(ref) {
      const current = this.getComponent(ref) ?? '';

      // Auto-expand years
      if (this.isYearRef(ref)) {
        const yearText = digitsOnly(current)
        if (yearText && yearText.length < 4) {
          this.setComponent(ref, expandTwoDigitYear(yearText))
        }
      }
    },
    
    inputKey(ref, e) {
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }

      const key = e.key
      const value = e.target.value ?? ''
      const start = e.target.selectionStart ?? 0
      const end = e.target.selectionEnd ?? 0
      const prev = this.prevRef(ref)
      const next = this.nextRef(ref)

      if (['/', '-', '.'].includes(key)) {
        if (next) {
          e.preventDefault()
          this.focusComponent(next)
        }
        return
      }

      if (key === 'ArrowLeft' && prev && start === 0 && end === 0) {
        e.preventDefault()
        this.focusComponent(prev, 'end')
        return
      }

      if (key === 'ArrowRight' && next && start === value.length && end === value.length) {
        e.preventDefault()
        this.focusComponent(next, 'start')
        return
      }

      if (key === 'Backspace' && prev && !value && start === 0 && end === 0) {
        e.preventDefault()
        this.focusComponent(prev, 'end')
        return
      }

      if (key.length === 1 && !/^\d$/.test(key)) {
        e.preventDefault()
      }
    },

    inputValue(ref, e) {
      const raw = e.target.value ?? ''

      if (this.applyDateText(raw)) {
        return
      }

      const nextValue = this.normalizeComponentValue(ref, raw)
      if (raw !== nextValue) {
        e.target.value = nextValue
      }

      this.setComponent(ref, nextValue)

      if (
        !this.isYearRef(ref) &&
        (
          nextValue.length >= this.componentMaxLength(ref) ||
          this.shouldAdvanceEarly(ref, nextValue, e)
        )
      ) {
        const next = this.nextRef(ref)
        if (next) this.focusComponent(next)
      }
    },

    inputPaste(ref, e) {
      const text = e.clipboardData?.getData('text') ?? ''
      if (!text) return

      if (this.applyDateText(text)) {
        return
      }

      const nextValue = this.normalizeComponentValue(ref, text)
      this.setComponent(ref, nextValue)

      if (nextValue.length >= this.componentMaxLength(ref) && !this.isYearRef(ref)) {
        const next = this.nextRef(ref)
        if (next) this.focusComponent(next)
      }
    },

    applyDateText(text) {
      const parsed = parseDateText(text, this.resolvedLayout)
      if (!parsed) return false

      this.setDateParts(parsed)
      return true
    },

    sync() {
      if (!this.hasAnyValue) {
        this.valid = false
        this.dateValue = undefined
        this.stringValue = ''
        return false
      }

      const year = Number(this.y)
      const month = Number(this.m)
      const day = Number(this.d)

      if (!year || !month || !day || month > 12 || day > 31) {
        this.valid = false
        this.dateValue = undefined
        this.stringValue = ''
        return false
      }

      const dateValue = new Date(year, month - 1, day)
      const isExactDate =
        !Number.isNaN(dateValue.getTime()) &&
        dateValue.getFullYear() === year &&
        dateValue.getMonth() === month - 1 &&
        dateValue.getDate() === day

      if (!isExactDate) {
        this.valid = false
        this.dateValue = undefined
        this.stringValue = ''
        return false
      }

      this.valid = true
      this.stringValue =
        String(year).padStart(4, '0') + '-' +
        String(month).padStart(2, '0') + '-' +
        String(day).padStart(2, '0')
      this.dateValue = dateValue

      return true
    }
  }
}
</script>
